import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, CreditCard, Truck, Shield, ArrowLeft, ArrowRight, ChevronDown, Tag, Sparkles } from 'lucide-react';
import { soundEffects } from '../components/audio/SoundEffects';
import { supabase } from '../supabaseClient';
import { trackEvent } from '../utils/Tracking';
import { storeConfig } from '../data/config';

const Checkout = () => {
    const { cartItems, cartTotal, clearCart } = useCart();

    useEffect(() => {
        trackEvent('InitiateCheckout', {
            num_items: cartItems.length,
            value: cartTotal,
            currency: 'EUR'
        });
    }, []);
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: Info, 2: Payment, 3: Success
    const [loading, setLoading] = useState(false);
    const [isMobileSummaryOpen, setIsMobileSummaryOpen] = useState(false);

    const total = cartItems.reduce((acc, item) => acc + (item.price * (item.qty || 1)), 0);
    const subscriptionTotal = cartItems.filter(item => item.isSubscription).reduce((acc, item) => acc + (item.price * (item.qty || 1)), 0);

    const [formData, setFormData] = useState({
        firstName: '', lastName: '', address: '', city: '', zip: '', email: ''
    });

    const [couponCode, setCouponCode] = useState('');
    const [discount, setDiscount] = useState(0);
    const [isApplying, setIsApplying] = useState(false);
    const [couponError, setCouponError] = useState('');
    const submissionLockRef = useRef(false);
    const activeSubmissionIdRef = useRef('');

    // --- Abandoned Cart Tracking ---
    const trackDraftOrder = async (email) => {
        if (!email || cartItems.length === 0) return;

        try {
            const sessionId = localStorage.getItem('visitor_session_id');
            const { error } = await supabase
                .from('orders')
                .upsert({
                    id: sessionId ? `draft_${sessionId}` : undefined, // Potential custom prefix to avoid ID collisions
                    customer_email: email,
                    customer_name: `${formData.firstName} ${formData.lastName}`.trim() || 'Potential Customer',
                    items: cartItems,
                    total_amount: finalTotal,
                    status: 'abandoned_draft', // Specialized status for retargeting
                    metadata: {
                        discount: discount,
                        coupon: couponCode,
                        is_draft: true
                    },
                    updated_at: new Date().toISOString()
                }, { onConflict: 'id' });

            if (error) console.error("Draft error:", error);
        } catch (err) {
            console.error("Failed to track draft:", err);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Auto-track if email is reasonably complete
        if (name === 'email' && value.includes('@') && value.length > 5) {
            const timer = setTimeout(() => trackDraftOrder(value), 1000);
            return () => clearTimeout(timer);
        }
    };

    const applyCoupon = () => {
        if (!couponCode) return;
        setIsApplying(true);
        setCouponError('');

        setTimeout(() => {
            const code = couponCode.trim().toUpperCase();
            const coupon = storeConfig.coupons.find(c => code.startsWith(c.code));

            if (coupon) {
                setDiscount(0);
                setCouponError('CODE NON PRIS EN CHARGE SUR CE PAIEMENT');
                soundEffects.play('click');
            } else {
                setDiscount(0);
                setCouponError('CODE INVALIDE');
                soundEffects.play('click');
            }
            setIsApplying(false);
        }, 800);
    };

    const shippingConfig = storeConfig.shipping || {};
    const freeShippingThreshold = Number(shippingConfig.freeThreshold) || 0;
    const standardShippingCost = Number(shippingConfig.standardCost) || 0;
    const discountAmount = total * discount;
    const shippingAmount = freeShippingThreshold > 0 && total >= freeShippingThreshold ? 0 : standardShippingCost;
    const finalTotal = total - discountAmount + shippingAmount;
    const isFreeShipping = shippingAmount === 0;

    const createSubmissionId = () => {
        if (globalThis.crypto?.randomUUID) {
            return `checkout_${globalThis.crypto.randomUUID()}`;
        }

        return `checkout_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        handlePayment();
    };

    const handlePayment = async () => {
        if (submissionLockRef.current) {
            return;
        }

        submissionLockRef.current = true;
        const submissionId = activeSubmissionIdRef.current || createSubmissionId();
        activeSubmissionIdRef.current = submissionId;
        soundEffects.play('click');
        setLoading(true);

        try {
            // 1. Create Checkout Session via Backend API
            const response = await fetch('/api/create-checkout-session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: cartItems,
                    customerEmail: formData.email,
                    customerName: `${formData.firstName} ${formData.lastName}`,
                    submissionId,
                    metadata: {
                        address: formData.address,
                        city: formData.city,
                        zip: formData.zip
                    }
                })
            });

            const data = await response.json();

            if (!response.ok) throw new Error(data.error);

            // 2. Track Event
            trackEvent('Purchase', {
                value: finalTotal,
                currency: 'EUR'
            });

            // 3. Redirect to Stripe
            if (data.url) {
                window.location.href = data.url;
            } else {
                throw new Error("Pas d'URL de redirection reçue");
            }

        } catch (error) {
            console.error('Erreur Paiement:', error);
            activeSubmissionIdRef.current = '';
            submissionLockRef.current = false;
            alert("Erreur lors de l'initialisation du paiement: " + error.message);
            setLoading(false);
        }
    };

    if (cartItems.length === 0 && step !== 3) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center text-black bg-white">
                <h2 className="text-3xl font-bold mb-4">Votre panier est vide</h2>
                <button
                    onClick={() => navigate('/shop')}
                    className="px-8 py-3 bg-[var(--primary)] text-black font-bold rounded-full hover:scale-105 transition-transform"
                >
                    Retourner à la boutique
                </button>
            </div>
        );
    }

    // Dynamic Theme Logic
    const lastItem = cartItems.length > 0 ? cartItems[cartItems.length - 1] : null;
    const activeTheme = lastItem?.theme || '#00f2ea';

    return (
        <div
            className="min-h-screen pt-24 pb-12 px-6 bg-black text-white overflow-hidden relative"
            style={{
                '--dynamic-theme': activeTheme,
                backgroundImage: `radial-gradient(circle at 50% 0%, ${activeTheme}22 0%, #000000 60%)`
            }}
        >
            {/* Ambient Background Glow */}
            <div className="fixed inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-[800px] h-[800px] bg-[var(--dynamic-theme)] blur-[150px] opacity-15 rounded-full mix-blend-screen animate-pulse" />
            </div>

            {/* --- MOBILE ORDER SUMMARY ACCORDION --- */}
            <div className="max-w-6xl mx-auto md:hidden mb-6 relative z-10">
                <button
                    onClick={() => setIsMobileSummaryOpen(!isMobileSummaryOpen)}
                    className="w-full flex items-center justify-between py-4 text-white font-black uppercase tracking-wider text-xs bg-white/5 border border-white/10 px-6 rounded-xl active:scale-[0.98] transition-all"
                    style={{ borderColor: isMobileSummaryOpen ? activeTheme : 'rgba(255,255,255,0.1)' }}
                >
                    <div className="flex items-center gap-2">
                        <span className="text-primary" style={{ color: activeTheme }}>
                            {isMobileSummaryOpen ? 'Masquer' : 'Afficher'} le récapitulatif
                        </span>
                        <ChevronDown
                            size={16}
                            className={`transition-transform duration-300 ${isMobileSummaryOpen ? 'rotate-180' : ''}`}
                            style={{ color: activeTheme }}
                        />
                    </div>
                    <span className="text-lg">{finalTotal.toFixed(2)}€</span>
                </button>

                <AnimatePresence>
                    {isMobileSummaryOpen && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden bg-black/40 backdrop-blur-xl border-x border-b border-white/10 rounded-b-xl -mt-2 pt-4"
                        >
                            <div className="p-6 space-y-6">
                                {cartItems.map((item, idx) => (
                                    <div key={idx} className="flex justify-between items-start border-b border-white/5 pb-6 last:border-0 group">
                                        <div className="flex gap-4">
                                            <div className="w-16 h-16 bg-white/5 rounded-xl flex items-center justify-center p-2 border border-white/5 group-hover:border-white/20 transition-colors">
                                                <img src={item.image} alt={item.name} className="w-full h-full object-contain drop-shadow-[0_0_10px_rgba(255,255,255,0.2)]" />
                                            </div>
                                            <div>
                                                <p className="font-bold text-white text-sm uppercase tracking-wide">{item.label || item.title}</p>
                                                <div className="flex items-center gap-3 mt-2">
                                                    <span className="text-[9px] font-bold bg-white/10 px-2 py-1 rounded text-white/60 uppercase tracking-wider">Qté: {item.qty}</span>
                                                    {item.details?.style && <span className="text-[9px] font-bold bg-white/5 px-2 py-1 rounded text-white/40 uppercase tracking-wider">{item.details.style}</span>}
                                                </div>
                                            </div>
                                        </div>
                                        <p className="font-bold text-lg font-mono" style={{ color: activeTheme }}>{item.price.toFixed(2)}€</p>
                                    </div>
                                ))}

                                {/* --- PROMO CODE (MOBILE) --- */}
                                <div className="pt-6 border-t border-white/10">
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black tracking-widest text-white/40 uppercase">Code Privilège</label>
                                        <div className="flex gap-2">
                                            <div className="relative flex-grow">
                                                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" size={14} />
                                                <input
                                                    type="text"
                                                    value={couponCode}
                                                    onChange={(e) => setCouponCode(e.target.value)}
                                                    placeholder="CODE"
                                                    className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-[10px] font-mono tracking-widest outline-none focus:border-[var(--dynamic-theme)] transition-all uppercase"
                                                />
                                            </div>
                                            <button
                                                onClick={applyCoupon}
                                                disabled={isApplying || !couponCode}
                                                className="px-4 rounded-xl bg-white/10 hover:bg-white/20 text-white text-[9px] font-black tracking-widest uppercase transition-all disabled:opacity-30"
                                            >
                                                {isApplying ? '...' : 'OK'}
                                            </button>
                                        </div>
                                        {couponError && <p className="text-[9px] font-black text-red-500 tracking-widest">{couponError}</p>}
                                        {discount > 0 && (
                                            <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 text-[9px] font-black text-green-400 tracking-widest">
                                                <Sparkles size={12} />
                                                -{(discount * 100)}% APPLIQUÉ
                                            </motion.div>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-3 py-4 border-t border-white/10">
                                    <div className="flex justify-between text-xs text-white/40 font-bold uppercase tracking-wider">
                                        <span>Sous-total</span>
                                        <span>{total.toFixed(2)}€</span>
                                    </div>
                                    {discount > 0 && (
                                        <div className="flex justify-between text-xs text-green-400 font-bold uppercase tracking-wider">
                                            <span>Réduction</span>
                                            <span>-{discountAmount.toFixed(2)}€</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-xs text-white/40 font-bold uppercase tracking-wider">
                                        <span>Livraison</span>
                                        <span className="text-white">{isFreeShipping ? 'Offerte' : `${shippingAmount.toFixed(2)}€`}</span>
                                    </div>
                                    <div className="flex justify-between text-sm text-white font-black uppercase tracking-wider pt-2 border-t border-white/5">
                                        <span>Total</span>
                                        <span style={{ color: activeTheme }}>{finalTotal.toFixed(2)}€</span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 relative z-10">

                {/* Left Column: Form */}
                <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-8"
                >
                    <button onClick={() => navigate('/shop')} className="flex items-center gap-2 text-white/50 hover:text-white transition-colors font-black uppercase tracking-widest text-xs">
                        <ArrowLeft size={16} />
                        Retour au Configurator
                    </button>

                    {step === 1 && (
                        <div className="bg-black/40 backdrop-blur-xl p-8 rounded-[32px] border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.5)] relative overflow-hidden group">
                            <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

                            <h2 className="text-2xl font-black mb-8 flex items-center gap-4 text-white uppercase tracking-[0.2em] border-b border-white/10 pb-4">
                                <Truck style={{ color: activeTheme }} size={24} />
                                Expédition
                            </h2>

                            {/* --- CHOIX AMAZON VS DIRECT --- */}
                            <div className="grid gap-4 mb-8">
                                <a
                                    href="https://www.amazon.fr/s?k=Unbox+Creator"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full py-5 bg-[#FF9900] hover:bg-[#ffad33] text-black font-black text-lg rounded-2xl flex items-center justify-center gap-3 shadow-lg hover:scale-[1.01] transition-all uppercase tracking-wide"
                                >
                                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                                        <path d="M11.578 14.93c-2.87 1.258-5.385 1.054-7.468.046.29 1.156.666 2.032.666 2.032s.672-.198 1.154-.344c.483-.145 1.309-.3 1.309-.3s1.205 1.545 3.39 1.545c1.474 0 2.508-.43 3.32-.962 1.096-.718 1.76-2.025 1.623-2.316-.27-.577-3.994.298-3.994.298zM19.1 8c-3.1 0-5.746.85-6.837 3.513l1.826.974c.465-1.53.943-2.484 2.809-2.28 1.866.204.996 2.122.996 2.122s-1.801.35-4.102.66c-2.48.337-3.52 1.89-3.323 3.333.197 1.442 1.706 2.375 3.517 2.112 1.81-.264 2.766-1.554 2.766-1.554s.22.995.342 1.372c.123.376.674.37.674.37l.872-4.188c.28-1.346.257-2.618-.4-3.824-1.026-1.876-2.404-2.61-4.14-2.61zm-4.706 8.358c-.524.587-1.472.636-1.92.204-.447-.432-.303-1.611.879-1.996 1.182-.385 2.17-.41 2.17-.41s-.292 1.365-1.129 2.202z" />
                                    </svg>
                                    Amazon Prime
                                </a>

                                <div className="relative flex py-2 items-center">
                                    <div className="flex-grow border-t border-white/5"></div>
                                    <span className="flex-shrink-0 mx-4 text-white/30 text-[10px] font-black tracking-widest uppercase">OU EXPRESS</span>
                                    <div className="flex-grow border-t border-white/5"></div>
                                </div>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-[10px] uppercase font-bold tracking-wider text-white/40 ml-1">Prénom</label>
                                        <input
                                            required name="firstName" value={formData.firstName} onChange={handleInputChange} type="text"
                                            className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm outline-none transition-all text-white placeholder-white/20 focus:border-[var(--dynamic-theme)] focus:bg-[var(--dynamic-theme)]/5 focus:shadow-[0_0_20px_rgba(0,0,0,0.2)]"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] uppercase font-bold tracking-wider text-white/40 ml-1">Nom</label>
                                        <input
                                            required name="lastName" value={formData.lastName} onChange={handleInputChange} type="text"
                                            className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm outline-none transition-all text-white placeholder-white/20 focus:border-[var(--dynamic-theme)] focus:bg-[var(--dynamic-theme)]/5 focus:shadow-[0_0_20px_rgba(0,0,0,0.2)]"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] uppercase font-bold tracking-wider text-white/40 ml-1">Adresse</label>
                                    <input
                                        required name="address" value={formData.address} onChange={handleInputChange} type="text"
                                        className="w-full bg-white/5 border border-white/10 rounded-xl p-4 outline-none transition-all text-white placeholder-white/20 focus:border-[var(--dynamic-theme)] focus:bg-[var(--dynamic-theme)]/5 focus:shadow-[0_0_20px_rgba(0,0,0,0.2)]"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-[10px] uppercase font-bold tracking-wider text-white/40 ml-1">Ville</label>
                                        <input
                                            required name="city" value={formData.city} onChange={handleInputChange} type="text"
                                            className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm outline-none transition-all text-white placeholder-white/20 focus:border-[var(--dynamic-theme)] focus:bg-[var(--dynamic-theme)]/5 focus:shadow-[0_0_20px_rgba(0,0,0,0.2)]"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] uppercase font-bold tracking-wider text-white/40 ml-1">Code Postal</label>
                                        <input
                                            required name="zip" value={formData.zip} onChange={handleInputChange} type="text"
                                            className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm outline-none transition-all text-white placeholder-white/20 focus:border-[var(--dynamic-theme)] focus:bg-[var(--dynamic-theme)]/5 focus:shadow-[0_0_20px_rgba(0,0,0,0.2)]"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] uppercase font-bold tracking-wider text-white/40 ml-1">Email</label>
                                    <input
                                        required name="email" value={formData.email} onChange={handleInputChange} type="email"
                                        className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm outline-none transition-all text-white placeholder-white/20 focus:border-[var(--dynamic-theme)] focus:bg-[var(--dynamic-theme)]/5 focus:shadow-[0_0_20px_rgba(0,0,0,0.2)]"
                                    />
                                </div>

                                {/* --- PROMO CODE (MOBILE FLOW STEP 1) --- */}
                                <div className="md:hidden pt-4 border-t border-white/5 mb-4">
                                    {!discount ? (
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={couponCode}
                                                onChange={(e) => setCouponCode(e.target.value)}
                                                placeholder="CODE PROMO ?"
                                                className="flex-grow bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-[10px] font-mono tracking-widest outline-none focus:border-[var(--dynamic-theme)] transition-all uppercase"
                                            />
                                            <button
                                                type="button"
                                                onClick={applyCoupon}
                                                className="px-4 rounded-xl bg-white/10 text-white text-[9px] font-black tracking-widest uppercase transition-all"
                                            >
                                                OK
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between text-[10px] font-black text-green-400 bg-green-500/10 p-3 rounded-xl border border-green-500/20">
                                            <div className="flex items-center gap-2">
                                                <Sparkles size={12} />
                                                <span>RÉDUCTION {(discount * 100)}% ACTIVÉE</span>
                                            </div>
                                            <button type="button" onClick={() => setDiscount(0)} className="text-white/40 hover:text-white underline text-[9px]">Annuler</button>
                                        </div>
                                    )}
                                    {couponError && <p className="mt-2 text-[9px] font-black text-red-500 tracking-widest">{couponError}</p>}
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="group relative w-full h-16 lg:h-20 rounded-xl lg:rounded-2xl bg-black text-white font-black tracking-[0.3em] lg:tracking-[0.5em] text-[10px] lg:text-xs uppercase flex items-center justify-center gap-3 lg:gap-4 overflow-hidden border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] active:scale-[0.95] transition-all duration-300 mt-8"
                                >
                                    {/* Liquid Metal Gradient Background */}
                                    <div className="absolute inset-0 opacity-40 group-hover:opacity-100 transition-opacity duration-700 bg-[linear-gradient(110deg,#000_30%,#333_50%,#000_70%)] bg-[length:200%_100%] animate-shimmer" />

                                    {/* Color Accent Glow */}
                                    <div className="absolute inset-x-0 bottom-0 h-[2px] opacity-50 blur-[2px]" style={{ backgroundColor: activeTheme }} />

                                    <div className="flex items-center gap-3 z-10">
                                        {loading ? (
                                            <>
                                                <span className="animate-pulse">TRAITEMENT...</span>
                                                <div className="w-2 h-2 bg-white rounded-full animate-ping" />
                                            </>
                                        ) : (
                                            <>
                                                <span className="text-white drop-shadow-[0_0_5px_rgba(255,255,255,0.5)]">PAYER {finalTotal.toFixed(2)}€</span>
                                                <ArrowRight size={18} className="group-hover:translate-x-3 transition-transform duration-500 text-primary" style={{ color: activeTheme }} />
                                            </>
                                        )}
                                    </div>
                                </button>
                            </form>
                        </div>
                    )}



                    {step === 3 && (
                        <div className="bg-black/40 backdrop-blur-xl p-12 rounded-[32px] border text-center shadow-[0_0_60px_rgba(0,0,0,0.5)] relative overflow-hidden"
                            style={{ borderColor: activeTheme }}>
                            <div className="absolute inset-0 bg-[url('/assets/noise.webp')] opacity-20" />
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 shadow-[0_0_40px_rgba(0,0,0,0.5)] relative z-10"
                                style={{ backgroundColor: activeTheme, boxShadow: `0 0 40px ${activeTheme}` }}
                            >
                                <CheckCircle size={48} className="text-black" />
                            </motion.div>
                            <h2 className="text-4xl font-black text-white mb-6 uppercase italic tracking-tighter relative z-10">Commande Validée</h2>
                            <p className="text-white/60 mb-10 font-mono text-sm relative z-10">Un email de confirmation quantique a été envoyé.</p>
                            <button
                                onClick={() => navigate('/')}
                                className="px-10 py-4 border-2 rounded-xl font-black uppercase tracking-[0.2em] transition-all relative z-10 hover:scale-105"
                                style={{
                                    borderColor: activeTheme,
                                    color: activeTheme,
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = activeTheme;
                                    e.currentTarget.style.color = 'black';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = activeTheme;
                                }}
                            >
                                Retour Base
                            </button>
                        </div>
                    )}
                </motion.div>

                {/* Right Column: Order Summary */}
                {
                    step !== 3 && (
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                            className="hidden md:block h-fit bg-black/40 backdrop-blur-xl p-8 rounded-[32px] border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.5)] sticky top-24"
                            style={{ borderColor: `${activeTheme}44` }}
                        >
                            <h3 className="text-xl font-black mb-8 text-white border-b border-white/10 pb-4 uppercase tracking-[0.2em]">Récapitulatif</h3>
                            <div className="space-y-6 mb-8 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                                {cartItems.map((item, idx) => (
                                    <div key={idx} className="flex justify-between items-start border-b border-white/5 pb-6 last:border-0 group">
                                        <div className="flex gap-4">
                                            <div className="w-16 h-16 bg-white/5 rounded-xl flex items-center justify-center p-2 border border-white/5 group-hover:border-white/20 transition-colors">
                                                <img src={item.image} alt={item.name} className="w-full h-full object-contain drop-shadow-[0_0_10px_rgba(255,255,255,0.2)]" />
                                            </div>
                                            <div>
                                                <p className="font-bold text-white text-lg uppercase tracking-wide">{item.label || item.title}</p>
                                                <div className="flex items-center gap-3 mt-2">
                                                    <span className="text-[10px] font-bold bg-white/10 px-2 py-1 rounded text-white/60 uppercase tracking-wider">Qté: {item.qty}</span>
                                                    {item.details?.style && <span className="text-[10px] font-bold bg-white/5 px-2 py-1 rounded text-white/40 uppercase tracking-wider">{item.details.style}</span>}
                                                </div>
                                                {item.isSubscription && <span className="text-[9px] font-black bg-[var(--dynamic-theme)] text-black px-2 py-1 rounded-sm inline-block mt-3 tracking-widest uppercase" style={{ backgroundColor: activeTheme }}>ABONNEMENT</span>}
                                            </div>
                                        </div>
                                        <p className="font-bold text-xl font-mono" style={{ color: activeTheme }}>{item.price.toFixed(2)}€</p>
                                    </div>
                                ))}
                            </div>

                            {/* --- PROMO CODE SECTION --- */}
                            <div className="mt-8 pt-6 border-t border-white/10">
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black tracking-widest text-white/40 uppercase">Code Privilège</label>
                                    <div className="flex gap-2">
                                        <div className="relative flex-grow">
                                            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" size={14} />
                                            <input
                                                type="text"
                                                value={couponCode}
                                                onChange={(e) => setCouponCode(e.target.value)}
                                                placeholder="ENTRER VOTRE CODE"
                                                className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-xs font-mono tracking-widest outline-none focus:border-[var(--dynamic-theme)] transition-all uppercase"
                                            />
                                        </div>
                                        <button
                                            onClick={applyCoupon}
                                            disabled={isApplying || !couponCode}
                                            className="px-6 rounded-xl bg-white/10 hover:bg-white/20 text-white text-[10px] font-black tracking-widest uppercase transition-all disabled:opacity-30"
                                        >
                                            {isApplying ? '...' : 'APPLIQUER'}
                                        </button>
                                    </div>
                                    {couponError && <p className="text-[9px] font-black text-red-500 tracking-widest">{couponError}</p>}
                                    {discount > 0 && (
                                        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 text-[9px] font-black text-green-400 tracking-widest">
                                            <Sparkles size={12} />
                                            RÉDUCTION DE {(discount * 100)}% APPLIQUÉE
                                        </motion.div>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-3 py-6 border-t border-white/10 mt-6">
                                <div className="flex justify-between text-sm text-white/40 font-bold uppercase tracking-wider">
                                    <span>Sous-total</span>
                                    <span>{total.toFixed(2)}€</span>
                                </div>
                                {discount > 0 && (
                                    <div className="flex justify-between text-sm text-green-400 font-bold uppercase tracking-wider">
                                        <span>Réduction ({(discount * 100)}%)</span>
                                        <span>-{discountAmount.toFixed(2)}€</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-sm text-white/40 font-bold uppercase tracking-wider">
                                    <span>Livraison</span>
                                    <span className="text-white">{isFreeShipping ? 'Offerte' : `${shippingAmount.toFixed(2)}€`}</span>
                                </div>
                            </div>

                            <div className="flex justify-between items-center text-2xl font-black border-t border-white/10 pt-8 text-white uppercase tracking-wider">
                                <span>Total</span>
                                <span className="" style={{ color: activeTheme, textShadow: `0 0 20px ${activeTheme}66` }}>{finalTotal.toFixed(2)}€</span>
                            </div>
                        </motion.div>
                    )
                }
            </div>
            <style>{`
                .animate-shimmer {
                    animation: shimmer 3s linear infinite;
                }
                @keyframes shimmer {
                    from { background-position: 100% 0; }
                    to { background-position: -100% 0; }
                }
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: rgba(255, 255, 255, 0.05);
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.2);
                    border-radius: 10px;
                }
            `}</style>
        </div>
    );
};

export default Checkout;
